import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { api, fmtErr } from "../api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  ShieldCheck, FileText, Stamp, Landmark, Scale, ArrowRight, ArrowLeft,
  CheckCircle2, Loader2, UploadCloud, X, Paperclip,
} from "lucide-react";

export const SERVICES = [
  { id: "Property Verification", icon: ShieldCheck, desc: "AI verification of your property documents — Khata, RTC, EC, Sale Deed and more — with flagged issues and a structured report." },
  { id: "Document Support", icon: FileText, desc: "We obtain or prepare the property documents you need — e-Khata, EC, RTC, certified copies and more." },
  { id: "Registration Assistance", icon: Stamp, desc: "KYC, mutation filing, deed drafting support and Sub-Registrar coordination for your registration." },
  { id: "Loan Assistance", icon: Landmark, desc: "Get the complete document set lenders ask for — title chain, EC, Khata, tax receipts and plans." },
  { id: "Legal Review", icon: Scale, desc: "Professional legal review of the verification findings, title documents and agreements." },
];

const SERVICE_CONTENT = {
  "Property Verification": {
    title: "Documents needed for AI verification",
    subtitle: "Upload what you have — one by one or in bulk. Missing documents are flagged in the report.",
    items: [
      "Sale Deed / Gift Deed / Partition Deed (title document)",
      "Khata Certificate & Khata Extract (e-Khata)",
      "RTC (Pahani) — Bhoomi record",
      "Mutation Register (MR) extract",
      "Encumbrance Certificate (EC) — ideally 13–30 years",
      "Latest property tax paid receipts",
    ],
    type: "verification",
  },
  "Document Support": {
    title: "Documents we can obtain or prepare for you",
    subtitle: "Select what you need — our team will get it done.",
    items: [
      "e-Khata application / Khata transfer (BBMP / BDA)",
      "Encumbrance Certificate (EC) — via Kaveri Online",
      "RTC (Pahani) extract — Bhoomi records",
      "Certified copies from the Sub-Registrar office",
      "Khata extract & property tax receipts",
      "Building plan / sanction plan copies",
    ],
    type: "request",
    uploadHint: "Attach any property documents you already have — we'll tell you exactly what's missing.",
  },
  "Registration Assistance": {
    title: "Registration requirements we handle",
    subtitle: "Select what applies — attach existing documents if you have them.",
    items: [
      "KYC registration documents (buyer & seller ID/address proofs)",
      "Mutation (MR) file preparation & filing",
      "Sale Deed / Agreement drafting support",
      "Stamp duty calculation & e-stamping guidance",
      "Sub-Registrar office appointment & coordination",
    ],
    type: "request",
    uploadHint: "Attach existing documents (old deed, mutation records, KYC) — we'll prepare the registration paperwork from them.",
  },
  "Loan Assistance": {
    title: "What lenders typically ask for",
    subtitle: "Select what you need help arranging.",
    items: [
      "Title documents chain (Sale Deed / Gift Deed)",
      "Latest EC (13–30 years)",
      "Khata certificate & extract",
      "Latest property tax paid receipts",
      "Approved building / sanction plan",
      "Sale agreement / allotment letter (new property)",
      "NOCs / society documents (if applicable)",
    ],
    type: "request",
    uploadHint: "Attach any property documents you already have — we'll identify what's missing.",
  },
  "Legal Review": {
    title: "What the legal review covers",
    subtitle: "Select what you need reviewed.",
    items: [
      "Title scrutiny opinion on the verification report",
      "Review of AI-flagged discrepancies & risks",
      "Agreement to Sell / Sale Deed review",
      "POA / Gift Deed / Partition Deed review",
      "Encumbrance & litigation guidance",
    ],
    type: "request",
    uploadHint: "Attach the report or documents you want reviewed.",
  },
};

export const AUDIENCES = ["Developer / Builder", "Law Firm", "Bank / NBFC", "Other Business"];

export default function ServiceDialog({ open, onOpenChange, mode = "service", initialService = "" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("select"); // select | detail | form | done
  const [service, setService] = useState("");
  const [audience, setAudience] = useState("");
  const [selected, setSelected] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", email: "", company: "", message: "" });
  const [attach, setAttach] = useState(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const reset = () => {
    setStep("select"); setService(""); setAudience(""); setSelected([]);
    setForm({ name: "", phone: "", email: "", company: "", message: "" });
    setAttach(null);
  };

  // Open directly on a service's detail view when the user clicked a specific service
  useEffect(() => {
    if (!open) return;
    if (initialService && SERVICE_CONTENT[initialService]) {
      setService(initialService);
      setSelected(SERVICE_CONTENT[initialService].items);
      setStep("detail");
    } else if (mode === "contact") {
      setStep("form");
    } else {
      setStep("select");
    }
  }, [open, initialService, mode]);

  const pickService = (s) => {
    setService(s);
    setSelected(SERVICE_CONTENT[s]?.items || []);
    setStep("detail");
  };

  const continueFromDetail = () => {
    const content = SERVICE_CONTENT[service];
    if (content?.type === "verification") {
      onOpenChange(false);
      navigate(user ? "/dashboard?new=1" : "/register");
      return;
    }
    setStep("form");
  };

  const toggleItem = (item) =>
    setSelected((s) => (s.includes(item) ? s.filter((x) => x !== item) : [...s, item]));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      let attachments = [];
      if (attach && user) {
        const fd = new FormData();
        fd.append("file", attach);
        const { data } = await api.post("/contact/upload", fd);
        attachments = [{ path: data.path, filename: data.filename }];
      }
      const requested = selected.length ? `Requested: ${selected.join("; ")}` : "";
      const message = [requested, form.message.trim()].filter(Boolean).join("\n\n");
      await api.post("/contact", {
        kind: mode === "business" ? "business" : mode === "contact" ? "contact" : "service",
        service, audience, ...form, message, attachments,
      });
      setStep("done");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSending(false);
    }
  };

  const content = SERVICE_CONTENT[service] || {};

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setTimeout(reset, 200); }}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="service-dialog">
        {step === "select" && (
          <>
            <DialogHeader>
              <DialogTitle>{mode === "business" ? "PropLoX for Business" : "Choose the service you need"}</DialogTitle>
            </DialogHeader>
            {mode === "business" ? (
              <div className="space-y-3">
                <p className="text-sm text-slate-500">Tell us about your team and we'll set up a business walkthrough.</p>
                <div className="space-y-1.5">
                  <Label>You are a</Label>
                  <Select value={audience} onValueChange={setAudience}>
                    <SelectTrigger data-testid="business-audience-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>{AUDIENCES.map((a) => <SelectItem key={a} value={a} data-testid={`audience-${a.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{a}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button className="w-full" disabled={!audience} onClick={() => setStep("form")} data-testid="business-continue-button">
                  Continue <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="space-y-2" data-testid="service-select-list">
                {SERVICES.map((s) => (
                  <button key={s.id} onClick={() => pickService(s.id)} data-testid={`service-option-${s.id.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="flex w-full items-center gap-3 rounded-xl border border-slate-200 p-4 text-left transition-colors hover:border-blue-300 hover:bg-blue-50/50">
                    <s.icon className="h-5 w-5 shrink-0 text-blue-600" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-slate-900">{s.id}</span>
                      <span className="mt-0.5 block text-xs leading-relaxed text-slate-500">{s.desc}</span>
                    </span>
                    <ArrowRight className="h-4 w-4 shrink-0 text-slate-400" />
                  </button>
                ))}
                <p className="pt-1 text-center text-xs text-slate-400">You don't have to use every service to get started.</p>
              </div>
            )}
          </>
        )}

        {step === "detail" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <button onClick={() => setStep("select")} className="text-slate-400 hover:text-slate-700" data-testid="detail-back-button"><ArrowLeft className="h-4 w-4" /></button>
                {service}
              </DialogTitle>
            </DialogHeader>
            <div data-testid="service-detail">
              <p className="text-sm font-semibold text-slate-900">{content.title}</p>
              <p className="mt-0.5 text-xs text-slate-500">{content.subtitle}</p>
              <div className="mt-4 space-y-2.5" data-testid="service-checklist">
                {content.items.map((item, i) => (
                  <label key={item} className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-slate-200 bg-slate-50/60 px-3.5 py-2.5 hover:border-blue-200"
                    data-testid={`checklist-item-${i}`}>
                    {content.type === "request" ? (
                      <Checkbox checked={selected.includes(item)} onCheckedChange={() => toggleItem(item)} className="mt-0.5" data-testid={`checklist-checkbox-${i}`} />
                    ) : (
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                    )}
                    <span className="text-sm leading-snug text-slate-700">{item}</span>
                  </label>
                ))}
              </div>
              {content.type === "verification" && (
                <p className="mt-4 rounded-lg bg-blue-50 px-3.5 py-2.5 text-xs leading-relaxed text-blue-800" data-testid="verification-flow-note">
                  You'll log in, upload your documents (single or bulk), and start the AI verification. The report unlocks automatically once ready.
                </p>
              )}
              <Button className="mt-5 w-full" onClick={continueFromDetail} data-testid="service-continue-button">
                {content.type === "verification"
                  ? (user ? "Continue to Upload" : "Log in & Continue to Upload")
                  : `Request ${service}`}
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </div>
          </>
        )}

        {step === "form" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <button onClick={() => setStep(service && mode === "service" ? "detail" : "select")} className="text-slate-400 hover:text-slate-700" data-testid="enquiry-back-button"><ArrowLeft className="h-4 w-4" /></button>
                {mode === "business" ? `Business enquiry — ${audience}` : mode === "contact" ? "Contact PropLoX" : service}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="enquiry-form">
              {selected.length > 0 && mode !== "business" && mode !== "contact" && (
                <p className="rounded-lg bg-slate-50 px-3.5 py-2.5 text-xs leading-relaxed text-slate-600" data-testid="enquiry-selected-summary">
                  <b>Requested:</b> {selected.join("; ")}
                </p>
              )}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5"><Label>Name *</Label><Input required data-testid="enquiry-name-input" value={form.name} onChange={set("name")} /></div>
                <div className="space-y-1.5"><Label>Phone *</Label><Input required data-testid="enquiry-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91 …" /></div>
              </div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" data-testid="enquiry-email-input" value={form.email} onChange={set("email")} /></div>
              {mode === "business" && (
                <div className="space-y-1.5"><Label>Company / Firm</Label><Input data-testid="enquiry-company-input" value={form.company} onChange={set("company")} /></div>
              )}
              <div className="space-y-1.5">
                <Label>Anything else we should know?</Label>
                <Textarea rows={3} data-testid="enquiry-message-input" value={form.message} onChange={set("message")}
                  placeholder={mode === "business" ? "Number of projects, team size, current process…" : "Property location, timeline…"} />
              </div>
              {content.uploadHint && (
                <div className="space-y-1.5">
                  <Label>Attach a document (optional)</Label>
                  {user ? (
                    attach ? (
                      <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-2.5" data-testid="enquiry-attachment-preview">
                        <span className="flex items-center gap-2 truncate text-sm text-slate-700"><Paperclip className="h-4 w-4 shrink-0 text-slate-400" />{attach.name}</span>
                        <button type="button" onClick={() => { setAttach(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-slate-400 hover:text-red-500" data-testid="enquiry-attachment-remove"><X className="h-4 w-4" /></button>
                      </div>
                    ) : (
                      <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-blue-200 bg-blue-50/50 px-4 py-4 text-xs font-medium text-slate-600 hover:border-blue-400" data-testid="enquiry-attachment-dropzone">
                        <UploadCloud className="h-4 w-4 text-blue-600" /> {content.uploadHint}
                        <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" data-testid="enquiry-attachment-input"
                          onChange={(e) => setAttach(e.target.files?.[0] || null)} />
                      </label>
                    )
                  ) : (
                    <p className="rounded-lg bg-slate-50 px-3.5 py-2.5 text-xs text-slate-500" data-testid="enquiry-attachment-login-note">
                      Log in to attach documents — or our team will collect them after this request.
                    </p>
                  )}
                </div>
              )}
              <Button type="submit" className="w-full" disabled={sending} data-testid="enquiry-submit-button">
                {sending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</> : "Submit Request"}
              </Button>
            </form>
          </>
        )}

        {step === "done" && (
          <div className="flex flex-col items-center py-8 text-center" data-testid="enquiry-success">
            <CheckCircle2 className="h-12 w-12 text-emerald-500" />
            <p className="mt-3 font-semibold text-slate-900">Request received</p>
            <p className="mt-1 max-w-xs text-sm text-slate-500">Our team will contact you shortly to guide you to the next step.</p>
            <Button variant="outline" className="mt-5" onClick={() => { onOpenChange(false); setTimeout(reset, 200); }} data-testid="enquiry-done-button">Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
